import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { PortalShell } from "@/components/AppHeader";
import { MapView } from "@/components/MapView";
import { FareBreakdown } from "@/components/FareBreakdown";
import { StatusBadge } from "@/components/StatusBadge";
import { api, errMsg, fmtDate, money } from "@/lib/api";
import { FoodSummary, ChargePurchase } from "@/components/FoodRequest";
import { TipPrompt } from "@/components/TipPrompt";
import { RentalBreakdown } from "@/pages/customer/RentalPanel";
import { TripComms } from "@/components/TripComms";
import { useAuth } from "@/context/AuthContext";

const Metric = ({ label, value, testId }) => (
  <div className="rounded-xl bg-white/[0.03] p-4">
    <p className="lux-eyebrow">{label}</p>
    <p data-testid={testId} className="mt-2 font-serif text-2xl text-amber-50">{value}</p>
  </div>
);

const TS = [["created_at", "Requested"], ["paid_at", "Paid"], ["accepted_at", "Accepted"], ["arrived_at", "Driver arrived"], ["started_at", "Trip started"], ["completed_at", "Completed"], ["cancelled_at", "Cancelled"]];

export default function TripDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/trips/${id}`).then(({ data }) => setTrip(data)).catch((e) => setError({ code: e.response?.status, msg: errMsg(e) }));
  }, [id]);

  return (
    <PortalShell>
      <button onClick={() => window.history.back()} data-testid="trip-detail-back" className="mb-6 inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-200"><ArrowLeft className="h-4 w-4" /> Back</button>
      {error && (
        <div data-testid="trip-detail-error" className="lux-card mx-auto max-w-md p-10 text-center">
          <Lock className="mx-auto h-8 w-8 text-amber-400" />
          <p className="mt-4 font-serif text-2xl text-amber-50">{error.code === 403 ? "Access denied" : "Trip not found"}</p>
          <p className="mt-2 text-sm text-zinc-400" data-testid="trip-detail-error-message">{error.msg}</p>
          <Link to="/" className="btn-outline-gold mt-6 !py-2.5">Home</Link>
        </div>
      )}
      {trip && (
        <div data-testid="trip-detail" className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-semibold text-amber-50 sm:text-4xl">Trip details</h1>
            <StatusBadge status={trip.status} testId="trip-detail-status" />
            {trip.driver_id && trip.status !== "awaiting_payment" && <Link to={`/trips/${trip.id}/feedback`} data-testid="trip-feedback-button" className="btn-outline-gold ml-auto !py-2 text-sm">Trip feedback</Link>}
          </div>
          <p className="font-mono text-xs text-zinc-500" data-testid="trip-detail-id">#{trip.id}</p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Metric label="Mileage to pickup" value={trip.miles_to_pickup != null ? `${trip.miles_to_pickup} mi` : "—"} testId="detail-miles-to-pickup" />
            <Metric label="Trip mileage" value={`${trip.trip_miles} mi`} testId="detail-trip-miles" />
            <Metric label="Total mileage" value={trip.total_miles != null ? `${trip.total_miles} mi` : "—"} testId="detail-total-miles" />
            <Metric label="Payment" value={<><StatusBadge status={trip.payment.status} testId="detail-payment-status" />{trip.payment.method_label && <span data-testid="detail-payment-method" className="mt-1 block font-sans text-xs text-zinc-400">{trip.payment.method_label}</span>}</>} testId="detail-payment" />
          </div>
          {trip.payment.status === "awaiting_zelle" && trip.status === "awaiting_payment" && (
            <p data-testid="detail-zelle-waiting" className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">Waiting for Zelle payment · reference <span className="font-mono">{trip.payment.zelle_reference}</span>. Your driver is sent once we confirm it.</p>
          )}
          <div className="grid gap-6 lg:grid-cols-[1fr_minmax(0,420px)]">
            <MapView className="h-[320px] sm:h-[420px]" pickup={trip.pickup} stops={trip.stops} route={trip.route_geometry} customer={user?.role !== "customer" ? (trip.customer_location || trip.customer_last_location) : null} testId="trip-detail-map" />
            <div className="lux-card p-5 sm:p-6">{trip.kind === "rental" ? <RentalBreakdown fare={trip.fare} overtime={trip.overtime} /> : <FareBreakdown fare={trip.fare} miles={trip.trip_miles} minutes={trip.trip_minutes} wait={trip.wait} cancel={trip.cancel} />}</div>
          </div>
          {trip.kind === "rental" && <p data-testid="detail-rental-info" className="text-sm text-zinc-400">Hourly rental · {trip.rental?.hours}h · start {fmtDate(trip.rental?.start_at)} · {money(trip.fare.rate)}/hr</p>}
          <div className="grid gap-6 lg:grid-cols-2">
            <FoodSummary trip={trip} />
            {user.role === "customer" && <TipPrompt trip={trip} onDone={setTrip} />}
            {user.role === "admin" && <ChargePurchase trip={trip} onDone={setTrip} />}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="lux-card space-y-3 p-5 text-sm sm:p-6" data-testid="detail-stops">
              <p className="lux-eyebrow">Pickup & stops</p>
              <p className="text-zinc-200"><span className="mr-2 text-amber-400">P</span>{trip.pickup.address}</p>
              {trip.stops.map((s, i) => <p key={i} data-testid={`detail-stop-${i}`} className="text-zinc-400"><span className="mr-2 text-amber-400">{i + 1}</span>{s.address}</p>)}
              <div className="border-t border-white/5 pt-3 text-zinc-400">Customer: <span className="text-zinc-200">{trip.customer_name}</span> · Driver: <span className="text-zinc-200" data-testid="detail-driver-name">{trip.driver_name || "—"}</span>{trip.driver_vehicle && ` · ${trip.driver_vehicle}`}</div>
            </div>
            <div className="lux-card p-5 text-sm sm:p-6" data-testid="detail-timestamps">
              <p className="lux-eyebrow mb-3">Timeline</p>
              {TS.filter(([k]) => trip.timestamps?.[k]).map(([k, l]) => (
                <div key={k} className="flex justify-between border-b border-white/5 py-2 last:border-0"><span className="text-zinc-400">{l}</span><span className="text-zinc-200">{fmtDate(trip.timestamps[k])}</span></div>
              ))}
            </div>
          </div>
          {trip.status !== "awaiting_payment" && <TripComms trip={trip} user={user} />}
        </div>
      )}
    </PortalShell>
  );
}
